/**
 * Validates a redirect URL to prevent XSS and protocol-based attacks.
 * Allows:
 *   - Absolute URLs with http: or https: protocols
 *   - Relative paths (starting with /)
 * Rejects:
 *   - Dangerous schemes (javascript:, data:, vbscript:, etc.)
 *   - Protocol-relative URLs (//evil.com)
 *
 * Note: External domains with http/https are intentionally allowed
 * since this feature is used to redirect users to partner/integration
 * sites after form submission.
 */
export function isValidRedirectUrl(url: string): boolean {
  // Reject protocol-relative URLs (//evil.com)
  if (url.startsWith("//")) {
    return false;
  }

  // Allow relative paths
  if (url.startsWith("/")) {
    return true;
  }

  // For absolute URLs, only allow http/https protocols
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * Safely redirects to a URL if it passes validation.
 * Returns true if redirect was initiated, false if URL was invalid.
 */
export function safeRedirect(url: string): boolean {
  if (isValidRedirectUrl(url)) {
    window.location.href = url;
    return true;
  }
  return false;
}

/** Query param name used to preserve the pre-login destination. */
export const RETURN_TO_PARAM = "returnTo";

/**
 * Validates a path intended for post-login redirection. Stricter than
 * isValidRedirectUrl: only same-origin relative paths are allowed, because
 * honoring an external returnTo after login is a phishing vector. Login
 * paths are rejected so we never redirect back into the login flow after
 * the user just authenticated.
 */
export function isSafeReturnToPath(path: string | null | undefined): boolean {
  if (!path) return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  const pathOnly = path.split("?")[0].split("#")[0];
  const segments = pathOnly.split("/").filter(Boolean);
  // Reject traversal segments — the browser normalizes `/a/../b` to `/b`
  // when window.location.href is set, which would let a crafted returnTo
  // trick getReturnToTenant into extracting the wrong tenant slug and
  // bypass the cross-tenant guard.
  if (segments.includes("..") || segments.includes(".")) return false;
  const lastSegment = segments[segments.length - 1];
  if (lastSegment === "login") return false;
  return true;
}

/**
 * Minimal shape shared by URLSearchParams and next/navigation's
 * ReadonlyURLSearchParams — accepting this lets callers pass either.
 */
type SearchParamsLike = { get: (key: string) => string | null };

/**
 * Reads the `returnTo` param from a search-params-like object and returns
 * it only if it passes {@link isSafeReturnToPath}. Useful for the common
 * pattern at login/onboarding/sign-up pages that need to honor a caller-
 * supplied post-auth destination.
 */
export function getSafeReturnTo(
  searchParams: SearchParamsLike | null | undefined,
): string | null {
  const value = searchParams?.get(RETURN_TO_PARAM) ?? null;
  if (!isSafeReturnToPath(value)) return null;
  return value;
}

/**
 * Returns the first path segment of a safe returnTo — the tenant slug
 * the user was trying to reach. Returns null if the path is unsafe or
 * has no segments.
 */
export function getReturnToTenant(
  returnTo: string | null | undefined,
): string | null {
  if (!isSafeReturnToPath(returnTo)) return null;
  const pathOnly = (returnTo as string).split("?")[0].split("#")[0];
  return pathOnly.split("/").filter(Boolean)[0] ?? null;
}

/**
 * Returns true when `returnTo` targets a path whose first segment matches
 * `tenantSubDomain`. Use this before honoring `returnTo` post-login: if
 * the user authenticated as someone without access to the returnTo's
 * tenant, replaying that path would just bounce them back through the
 * tenant-access guards.
 */
export function isReturnToForTenant(
  returnTo: string | null | undefined,
  tenantSubDomain: string,
): boolean {
  return getReturnToTenant(returnTo) === tenantSubDomain;
}

/**
 * Appends a returnTo query param to the given base path when the target is
 * a safe relative path. Returns the base path unchanged otherwise.
 *
 * Used when redirecting to a gate page (login, onboarding, clinic-setup)
 * and we want the gate to forward the user to their original destination
 * on completion.
 */
export function withReturnTo(
  basePath: string,
  returnTo?: string | null,
): string {
  if (!isSafeReturnToPath(returnTo)) return basePath;
  const separator = basePath.includes("?") ? "&" : "?";
  return `${basePath}${separator}${RETURN_TO_PARAM}=${encodeURIComponent(
    returnTo as string,
  )}`;
}
